import React, { useState, useEffect } from "react";
import styles from "./LiveMapPage.module.css";

interface LiveMapPageProps {
  embedded?: boolean;
}

export default function LiveMapPage({ embedded = false }: LiveMapPageProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedCity, setSelectedCity] = useState("San Francisco, CA");
  const [activeFilter, setActiveFilter] = useState("Available");

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const trips = [
    {
      id: "#8821",
      type: "Standard",
      driver: "Sarah J.",
      status: "PICKUP",
      statusColor: "yellow",
      icon: "🚕",
      pickup: "800 Market St.",
      dropoff: "2400 Mission St.",
      borderHighlight: false
    },
    {
      id: "#8824",
      type: "EV Plus",
      driver: "Mike T.",
      status: "EN ROUTE",
      statusColor: "green",
      icon: "⚡",
      pickup: "SFO Terminal 2",
      dropoff: "Hyatt Regency",
      borderHighlight: true
    },
    {
      id: "#8819",
      type: "XL",
      driver: "Davide B.",
      status: "ARRIVING",
      statusColor: "green",
      icon: "🚐",
      pickup: "Chase Center",
      dropoff: "North Beach",
      borderHighlight: false
    },
    {
      id: "#8810",
      type: "Standard",
      driver: "Jenny L.",
      status: "EN ROUTE",
      statusColor: "green",
      icon: "🚕",
      pickup: "Golden Gate Park",
      dropoff: "Union Square",
      borderHighlight: false
    }
  ];

  return (
    <>
      {/* Top Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div>
            <h2 className={styles.pageTitle}>Live Map</h2>
            <p className={styles.pageSubtitle}>Real-time view of trips, drivers, and demand</p>
          </div>
        </div>

        <div className={styles.headerRight}>
          {/* City Selector */}
          <div className={styles.citySelector}>
            <select 
              value={selectedCity} 
              onChange={(e) => setSelectedCity(e.target.value)}
              className={styles.citySelect}
            >
              <option>San Francisco, CA</option>
              <option>Los Angeles, CA</option>
              <option>New York, NY</option>
              <option>Austin, TX</option>
            </select>
            <span className={styles.selectIcon}>▾</span>
          </div>

          {/* Live Indicator */}
          <div className={styles.liveIndicator}>
            <div className={styles.pulseContainer}>
              <span className={styles.pulseDot}></span>
              <span className={styles.pulseRing}></span>
            </div>
            <span className={styles.liveTime}>{formatTime(currentTime)}</span>
          </div>

          {/* Refresh Button */}
          <button className={styles.refreshBtn}>
            <span className={styles.refreshIcon}>↻</span>
          </button>
        </div>
      </header>

      {/* Main Content: Map + Sidebar */}
      <div className={styles.content}>
        {/* Map Area */}
        <div className={styles.mapContainer}>
          {/* Map Background */}
          <div className={styles.mapBackground}></div>
          
          {/* Map Gradient Overlay */}
          <div className={styles.mapOverlay}></div>

          {/* Driver Dots */}
          <div className={styles.driverDot} style={{ top: '33%', left: '25%' }}>
            <div className={styles.dot}></div>
            <div className={styles.tooltip}>
              <div className={styles.tooltipDriver}>Driver #492</div>
              <div className={styles.tooltipStatus}>Available</div>
            </div>
          </div>

          <div className={styles.driverDot} style={{ top: '45%', left: '35%' }}>
            <div className={styles.dot}></div>
            <div className={styles.tooltip}>
              <div className={styles.tooltipDriver}>Driver #358</div>
              <div className={styles.tooltipStatus}>Available</div>
            </div>
          </div>

          <div className={styles.driverDot} style={{ top: '55%', left: '50%' }}>
            <div className={styles.dot}></div>
            <div className={styles.tooltip}>
              <div className={styles.tooltipDriver}>Driver #721</div>
              <div className={styles.tooltipStatus}>En Route</div>
            </div>
          </div>

          <div className={styles.driverDot} style={{ top: '38%', left: '60%' }}>
            <div className={styles.dot}></div>
            <div className={styles.tooltip}>
              <div className={styles.tooltipDriver}>Driver #194</div>
              <div className={styles.tooltipStatus}>Available</div>
            </div>
          </div>

          {/* Trip Lines */}
          <svg className={styles.tripLines} viewBox="0 0 100 100" preserveAspectRatio="none">
            <line x1="25" y1="33" x2="50" y2="55" className={styles.tripLine} />
            <line x1="35" y1="45" x2="60" y2="38" className={styles.tripLine} />
          </svg>

          {/* Map Controls */}
          <div className={styles.mapControls}>
            <button className={styles.mapControlBtn}>+</button>
            <button className={styles.mapControlBtn}>−</button>
            <button className={styles.mapControlBtn}>⊕</button>
          </div>
        </div>

        {/* Right Sidebar */}
        <aside className={styles.sidebar}>
          {/* Live Stats */}
          <div className={styles.liveStats}>
            <div className={styles.statsHeader}>
              <span className={styles.statsIcon}>📊</span>
              <h3 className={styles.statsTitle}>LIVE STATS</h3>
            </div>

            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <p className={styles.statLabel}>Active Trips</p>
                <div className={styles.statValueRow}>
                  <span className={styles.statValue}>1,240</span>
                  <span className={styles.statChange}>↗ 12%</span>
                </div>
              </div>

              <div className={styles.statCard}>
                <p className={styles.statLabel}>Drivers Online</p>
                <div className={styles.statValueRow}>
                  <span className={styles.statValue}>458</span>
                  <span className={styles.statChangeNegative}>↘ 0%</span>
                </div>
              </div>

              <div className={styles.statCard}>
                <p className={styles.statLabel}>Avg Wait Time</p>
                <div className={styles.statValueRow}>
                  <span className={styles.statValue}>3.2m</span>
                  <span className={styles.statChangeNegative}>↓ 4%</span>
                </div>
              </div>

              <div className={styles.statCard}>
                <p className={styles.statLabel}>Trips / Min</p>
                <div className={styles.statValueRow}>
                  <span className={styles.statValue}>84</span>
                  <span className={styles.statChange}>↗ 2%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className={styles.filters}>
            <div className={styles.filterHeader}>
              <h3 className={styles.filterTitle}>FILTERS</h3>
              <span className={styles.filterIcon}>▴</span>
            </div>

            <div className={styles.filterGroup}>
              <p className={styles.filterLabel}>Driver Status</p>
              <div className={styles.filterButtons}>
                <button 
                  className={`${styles.filterBtn} ${activeFilter === 'Available' ? styles.filterBtnActive : ''}`}
                  onClick={() => setActiveFilter('Available')}
                >
                  Available
                </button>
                <button 
                  className={`${styles.filterBtn} ${activeFilter === 'Busy' ? styles.filterBtnActive : ''}`}
                  onClick={() => setActiveFilter('Busy')}
                >
                  Busy
                </button>
                <button 
                  className={`${styles.filterBtn} ${activeFilter === 'Offline' ? styles.filterBtnActive : ''}`}
                  onClick={() => setActiveFilter('Offline')}
                >
                  Offline
                </button>
              </div>
            </div>

            <div className={styles.filterGroup}>
              <p className={styles.filterLabel}>Zones</p>
              <select className={styles.zoneSelect}>
                <option>All Zones</option>
                <option>Downtown</option>
                <option>Airport</option>
                <option>Mission District</option>
              </select>
            </div>
          </div>

          {/* Active Trips Feed */}
          <div className={styles.tripsFeedHeader}>
            <h3 className={styles.tripsFeedTitle}>Active Trips Feed</h3>
            <span className={styles.liveBadge}>Live</span>
          </div>

          {/* Trips List */}
          <div className={styles.tripsList}>
            {trips.map((trip) => (
              <div 
                key={trip.id} 
                className={`${styles.tripCard} ${trip.borderHighlight ? styles.tripCardHighlight : ''}`}
              >
                <div className={styles.tripHeader}>
                  <div className={styles.tripInfo}>
                    <div className={styles.tripIcon}>{trip.icon}</div>
                    <div>
                      <p className={styles.tripId}>Trip {trip.id}</p>
                      <p className={styles.tripDriver}>
                        {trip.type} • <span className={styles.driverName}>{trip.driver}</span>
                      </p>
                    </div>
                  </div>
                  <span className={`${styles.tripStatus} ${styles[`status${trip.statusColor}`]}`}>
                    {trip.status}
                  </span>
                </div>

                <div className={styles.tripRoute}>
                  <div className={styles.routeLine}>
                    <div className={styles.routeDotStart}></div>
                    <div className={styles.routeConnector}></div>
                    <div className={styles.routeDotEnd}></div>
                  </div>
                  <div className={styles.routeAddresses}>
                    <p className={styles.routePickup}>{trip.pickup}</p>
                    <p className={styles.routeDropoff}>{trip.dropoff}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </>
  );
}